import { useEffect, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getChallengeForDate, getWeeklyChallengeForDate, getWeekStartDate } from '../data/challenges';
import { getLocalCalendarDay } from '../utils/dateUtils';

export const useDailyChallenge = (userSettings, setUserSettings) => {
    const { user } = useAuth();
    const today = getLocalCalendarDay();
    const weekStart = getWeekStartDate(today);

    // Optimistic / Local Definitions
    const localDailyDef = getChallengeForDate(today);
    const localWeeklyDef = getWeeklyChallengeForDate(today);

    // State for DB-backed challenges
    const [dbDailyState, setDbDailyState] = useState(null);
    const [dbWeeklyState, setDbWeeklyState] = useState(null);
    const [dbDailyDef, setDbDailyDef] = useState(null);
    const [dbWeeklyDef, setDbWeeklyDef] = useState(null);

    const [loading, setLoading] = useState(false);

    // Helper: Ensure System Challenge Exists
    const ensureSystemChallenge = useCallback(async (date, type, localDef) => {
        try {
            // 1. Try to find existing
            const { data: existing, error } = await supabase
                .from('system_challenges')
                .select('*')
                .eq('date', date)
                .eq('type', type)
                .maybeSingle();

            if (existing) return existing;

            // 2. If not found, create it based on local definition
            // We use 'upsert' with ignore duplicates to handle race conditions safely
            const { data: created, error: createError } = await supabase
                .from('system_challenges')
                .upsert({
                    type,
                    date,
                    reference_id: localDef.id,
                    config: {
                        target: localDef.target,
                        description: localDef.description,
                        mode: localDef.mode,
                        challenge_type: localDef.type
                    }
                }, { onConflict: 'date, type' })
                .select()
                .single();

            if (createError) throw createError;
            return created;

        } catch (err) {
            console.error(`Error ensuring system challenge (${type}):`, err);
            return null;
        }
    }, []);

    // Helper: Fetch User Progress
    const fetchUserProgress = useCallback(async (challengeId) => {
        try {
            const { data, error } = await supabase
                .from('user_system_challenges')
                .select('*')
                .eq('user_id', user.id)
                .eq('challenge_id', challengeId)
                .maybeSingle();

            if (error && error.code !== 'PGRST116') throw error; // Ignore not found
            return data || { progress: 0, completed: false };
        } catch (err) {
            console.error('Error fetching challenge progress:', err);
            return { progress: 0, completed: false };
        }
    }, [user]);

    // 1. Logic for Authenticated Users
    useEffect(() => {
        let mounted = true;

        const loadDbChallenges = async () => {
            if (!user) return;
            setLoading(true);

            // A. Daily
            const sysDaily = await ensureSystemChallenge(today, 'daily', localDailyDef);
            if (mounted && sysDaily) {
                setDbDailyDef({ ...localDailyDef, ...sysDaily.config, sysId: sysDaily.id }); // Merge config
                const prog = await fetchUserProgress(sysDaily.id);
                if (mounted) setDbDailyState(prog);
            }

            // B. Weekly
            const sysWeekly = await ensureSystemChallenge(weekStart, 'weekly', localWeeklyDef);
            if (mounted && sysWeekly) {
                setDbWeeklyDef({ ...localWeeklyDef, ...sysWeekly.config, sysId: sysWeekly.id });
                const prog = await fetchUserProgress(sysWeekly.id);
                if (mounted) setDbWeeklyState(prog);
            }

            if (mounted) setLoading(false);
        };

        loadDbChallenges();

        return () => { mounted = false; };
    }, [user, today, weekStart, ensureSystemChallenge, fetchUserProgress]); // Removed localDef deps as they correlate to dates

    // 2. Logic for Guest Users (Local Only)
    // This runs via standard React state updates in updateChallengeProgress if !user
    // But we also need to ensure userSettings init for guests
    useEffect(() => {
        if (user || !userSettings) return; // Skip if logged in or settings not ready

        setUserSettings(prev => {
            let next = { ...prev };
            let changed = false;

            // Daily Challenge
            if (!next.dailyChallenge || next.dailyChallenge.date !== today) {
                next.dailyChallenge = {
                    date: today,
                    id: localDailyDef.id,
                    progress: 0,
                    completed: false
                };
                changed = true;
            }

            // Weekly Challenge
            if (!next.weeklyChallenge || next.weeklyChallenge.weekStart !== weekStart) {
                next.weeklyChallenge = {
                    weekStart: weekStart,
                    id: localWeeklyDef.id,
                    progress: 0,
                    completed: false
                };
                changed = true;
            }

            return changed ? next : prev;
        });
    }, [user, userSettings, today, weekStart, localDailyDef.id, localWeeklyDef.id, setUserSettings]);


    // Combined Update Function
    const updateChallengeProgress = useCallback(async (event) => {
        const result = { dailyCompleted: false, weeklyCompleted: false };

        // Helper to check if modes match (including "Full" variants for standard challenges)
        const isMatchingMode = (targetMode, eventMode) => {
            if (targetMode === 'any') return true;
            if (targetMode === eventMode) return true;

            // Allow "Full" versions to count towards standard challenges
            if (targetMode === 'callsigns' && eventMode === 'callsigns_full') return true;
            if (targetMode === 'qcodes' && eventMode === 'qcodes_full') return true;
            if (targetMode === 'othercodes' && eventMode === 'othercodes_full') return true;

            // Bridge ProQSO and Standard QSO
            if (targetMode === 'qso' && (eventMode === 'pro_qso' || eventMode === 'pro_qso_complete')) return true;

            return false;
        };

        // Shared Logic to calculate progress
        const calculateNextState = (currentState, definition, evt) => {
            if (!currentState || currentState.completed) return null;

            let newProgress = currentState.progress || 0;
            let shouldUpdate = false;

            if (definition.type === 'count' && evt.type === 'count') {
                if (isMatchingMode(definition.mode, evt.mode)) {
                    newProgress += evt.amount;
                    shouldUpdate = true;
                }
            } else if (definition.type === 'sequence' && evt.type === 'sequence') {
                if (isMatchingMode(definition.mode, evt.mode)) {
                    newProgress += evt.amount;
                    shouldUpdate = true;
                }
            } else if (definition.type === 'score' && evt.type === 'score') {
                newProgress += evt.amount;
                shouldUpdate = true;
            } else if (definition.type === 'streak' && evt.type === 'streak') {
                if (evt.amount > newProgress) {
                    newProgress = evt.amount;
                    shouldUpdate = true;
                }
            } else if (definition.type === 'speed' && evt.type === 'count') {
                if (evt.wpm >= 25) {
                    newProgress += evt.amount;
                    shouldUpdate = true;
                }
            }

            if (shouldUpdate) {
                // Cap progress unless it's a streak
                const isComplete = newProgress >= definition.target;

                if (definition.type !== 'streak' && newProgress > definition.target) {
                    newProgress = definition.target;
                }

                return {
                    ...currentState,
                    progress: newProgress,
                    completed: isComplete
                };
            }
            return null;
        };

        if (user) {
            // --- DB Mode ---
            // 1. Update Daily
            if (dbDailyDef && dbDailyState && !dbDailyState.completed) {
                const nextState = calculateNextState(dbDailyState, dbDailyDef, event);
                if (nextState) {
                    // Update Local
                    setDbDailyState(prev => ({ ...prev, ...nextState }));
                    if (nextState.completed) result.dailyCompleted = true;

                    // Sync DB
                    await supabase.from('user_system_challenges').upsert({
                        user_id: user.id,
                        challenge_id: dbDailyDef.sysId,
                        progress: nextState.progress,
                        completed: nextState.completed,
                        completed_at: nextState.completed ? new Date().toISOString() : null,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'user_id, challenge_id' });
                }
            }

            // 2. Update Weekly
            if (dbWeeklyDef && dbWeeklyState && !dbWeeklyState.completed) {
                const nextState = calculateNextState(dbWeeklyState, dbWeeklyDef, event);
                if (nextState) {
                    setDbWeeklyState(prev => ({ ...prev, ...nextState }));
                    if (nextState.completed) result.weeklyCompleted = true;

                    await supabase.from('user_system_challenges').upsert({
                        user_id: user.id,
                        challenge_id: dbWeeklyDef.sysId,
                        progress: nextState.progress,
                        completed: nextState.completed,
                        completed_at: nextState.completed ? new Date().toISOString() : null,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'user_id, challenge_id' });
                }
            }

        } else {
            // --- Local Mode ---
            // Speculative check using prop (userSettings) for return value
            if (userSettings?.dailyChallenge && userSettings.dailyChallenge.date === today && !userSettings.dailyChallenge.completed) {
                const next = calculateNextState(userSettings.dailyChallenge, localDailyDef, event);
                if (next && next.completed) result.dailyCompleted = true;
            }
            if (userSettings?.weeklyChallenge && userSettings.weeklyChallenge.weekStart === weekStart && !userSettings.weeklyChallenge.completed) {
                const next = calculateNextState(userSettings.weeklyChallenge, localWeeklyDef, event);
                if (next && next.completed) result.weeklyCompleted = true;
            }

            setUserSettings(prev => {
                let next = { ...prev };
                let changed = false;

                if (next.dailyChallenge && next.dailyChallenge.date === today) {
                    const updated = calculateNextState(next.dailyChallenge, localDailyDef, event);
                    if (updated) {
                        next.dailyChallenge = updated;
                        changed = true;
                    }
                }

                if (next.weeklyChallenge && next.weeklyChallenge.weekStart === weekStart) {
                    const updated = calculateNextState(next.weeklyChallenge, localWeeklyDef, event);
                    if (updated) {
                        next.weeklyChallenge = updated;
                        changed = true;
                    }
                }

                return changed ? next : prev;
            });
        }

        return result;
    }, [user, userSettings, today, weekStart, dbDailyDef, dbDailyState, dbWeeklyDef, dbWeeklyState, localDailyDef, localWeeklyDef, setUserSettings]);

    // Return current state based on auth status
    if (user) {
        return {
            dailyChallenge: dbDailyState,
            dailyDefinition: dbDailyDef || localDailyDef, // Fallback to local def while loading DB def
            weeklyChallenge: dbWeeklyState,
            weeklyDefinition: dbWeeklyDef || localWeeklyDef,
            updateChallengeProgress,
            loading
        };
    } else {
        return {
            dailyChallenge: userSettings?.dailyChallenge,
            dailyDefinition: localDailyDef,
            weeklyChallenge: userSettings?.weeklyChallenge,
            weeklyDefinition: localWeeklyDef,
            updateChallengeProgress,
            loading: false
        };
    }
};
